<template>
  <v-dialog :model-value="modelValue" max-width="720" @update:model-value="$emit('update:modelValue', $event)">
    <v-card class="d-flex flex-column" style="max-height: 90vh;">
      <v-card-title class="d-flex align-center justify-space-between pa-4 flex-shrink-0">
        <span>MCP Setup Instructions</span>
        <v-btn icon variant="text" @click="$emit('update:modelValue', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider class="flex-shrink-0" />

      <v-card-text class="pa-4 flex-grow-1 overflow-y-auto">
        <p class="text-body-2 mb-4">
          The Model Context Protocol (MCP) allows AI assistants to access your Continuum Design workflows.
          Choose your client below and follow the setup instructions.
        </p>

        <v-expansion-panels variant="accordion">
          <!-- Claude Code -->
          <v-expansion-panel>
            <v-expansion-panel-title>
              <div class="d-flex align-center ga-2">
                <v-icon size="20">mdi-console</v-icon>
                <span class="font-weight-medium">Claude Code (CLI)</span>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <div class="step-section">
                <div class="step-header">
                  <v-avatar size="22" color="primary" class="step-badge">
                    <span class="text-caption font-weight-bold">1</span>
                  </v-avatar>
                  <span class="text-body-2 font-weight-medium">Connect MCP Server</span>
                </div>
                <p class="text-body-2 mb-3 mt-2">
                  Run this command in your terminal:
                </p>
                <div class="code-block-wrapper">
                  <pre class="code-block">{{ claudeCodeCommand }}</pre>
                  <v-btn
                    size="x-small"
                    variant="tonal"
                    class="copy-btn"
                    @click="copyToClipboard(claudeCodeCommand)"
                  >
                    <v-icon size="14">mdi-content-copy</v-icon>
                  </v-btn>
                </div>
                <p class="text-caption text-medium-emphasis mt-3">
                  Replace <code>YOUR_API_KEY</code> with your generated API key.
                </p>
              </div>

              <v-divider class="my-4" />

              <div class="step-section">
                <div class="step-header">
                  <v-avatar size="22" color="primary" class="step-badge">
                    <span class="text-caption font-weight-bold">2</span>
                  </v-avatar>
                  <span class="text-body-2 font-weight-medium">Install Plugins</span>
                  <v-chip size="x-small" variant="outlined" color="primary" class="ml-2">Optional</v-chip>
                </div>
                <p class="text-body-2 mb-3 mt-2">
                  Install Continuum Design plugins to enhance your AI assistant:
                </p>
                <div class="code-block-wrapper">
                  <pre class="code-block">{{ claudePluginCommands }}</pre>
                  <v-btn
                    size="x-small"
                    variant="tonal"
                    class="copy-btn"
                    @click="copyToClipboard(claudePluginCommands)"
                  >
                    <v-icon size="14">mdi-content-copy</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <!-- Cursor -->
          <v-expansion-panel>
            <v-expansion-panel-title>
              <div class="d-flex align-center ga-2">
                <v-icon size="20">mdi-cursor-default-outline</v-icon>
                <span class="font-weight-medium">Cursor</span>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <div class="step-section">
                <div class="step-header">
                  <v-avatar size="22" color="primary" class="step-badge">
                    <span class="text-caption font-weight-bold">1</span>
                  </v-avatar>
                  <span class="text-body-2 font-weight-medium">Connect MCP Server</span>
                </div>
                <p class="text-body-2 mb-3 mt-2">
                  Add this configuration to your Cursor MCP settings file
                  (<code>~/.cursor/mcp.json</code>):
                </p>
                <div class="code-block-wrapper">
                  <pre class="code-block">{{ cursorConfig }}</pre>
                  <v-btn
                    size="x-small"
                    variant="tonal"
                    class="copy-btn"
                    @click="copyToClipboard(cursorConfig)"
                  >
                    <v-icon size="14">mdi-content-copy</v-icon>
                  </v-btn>
                </div>
                <p class="text-caption text-medium-emphasis mt-3">
                  Replace <code>YOUR_API_KEY</code> with your generated API key.
                </p>
              </div>

              <v-divider class="my-4" />

              <div class="step-section">
                <div class="step-header">
                  <v-avatar size="22" color="primary" class="step-badge">
                    <span class="text-caption font-weight-bold">2</span>
                  </v-avatar>
                  <span class="text-body-2 font-weight-medium">Set up Skills</span>
                  <v-chip size="x-small" variant="outlined" color="primary" class="ml-2">Optional</v-chip>
                </div>
                <p class="text-body-2 mb-3 mt-2">
                  Clone the
                  <a :href="pluginsRepoUrl" target="_blank" rel="noopener">qlerify-plugins repository</a>
                  (or use GitHub's <strong>Code → Download ZIP</strong>):
                </p>
                <div class="code-block-wrapper">
                  <pre class="code-block">{{ cursorCloneCommand }}</pre>
                  <v-btn
                    size="x-small"
                    variant="tonal"
                    class="copy-btn"
                    @click="copyToClipboard(cursorCloneCommand)"
                  >
                    <v-icon size="14">mdi-content-copy</v-icon>
                  </v-btn>
                </div>

                <p class="text-body-2 mt-3 mb-2">
                  Copy skill folders from <code>plugins/mcp-companion/skills/</code> into
                  <strong>one</strong> of these locations:
                </p>
                <ul class="text-body-2 setup-steps">
                  <li>
                    <strong>Workspace skills</strong> — available only in this project.
                    Folder: <code>&lt;your-workspace&gt;/.cursor/skills/</code>
                  </li>
                  <li>
                    <strong>Global skills</strong> — available across every workspace.
                    Folder: <code>~/.cursor/skills/</code>
                  </li>
                </ul>
                <div class="code-block-wrapper mt-2">
                  <pre class="code-block">{{ cursorSkillsLayout }}</pre>
                </div>

                <p class="text-body-2 mt-3">
                  Then reload the window (<code>Cmd+Shift+P</code> →
                  <strong>Developer: Reload Window</strong>) so Cursor picks up the skills.
                </p>

                <p class="text-caption text-medium-emphasis mt-3">
                  See the
                  <a href="https://cursor.com/docs/context/skills" target="_blank" rel="noopener">official skills documentation</a>
                  for more details.
                </p>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <!-- Antigravity IDE -->
          <v-expansion-panel>
            <v-expansion-panel-title>
              <div class="d-flex align-center ga-2">
                <v-icon size="20">mdi-rocket-launch-outline</v-icon>
                <span class="font-weight-medium">Antigravity IDE</span>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <div class="step-section">
                <div class="step-header">
                  <v-avatar size="22" color="primary" class="step-badge">
                    <span class="text-caption font-weight-bold">1</span>
                  </v-avatar>
                  <span class="text-body-2 font-weight-medium">Open the raw MCP config</span>
                </div>
                <ol class="text-body-2 setup-steps mt-2">
                  <li>Open the Agent panel on the right side of the IDE</li>
                  <li>
                    Click the
                    <v-chip size="x-small" variant="tonal" color="primary" label class="dots-chip">···</v-chip>
                    button at the top of the panel
                  </li>
                  <li>Select <strong>MCP Servers</strong> from the dropdown</li>
                  <li>
                    Click
                    <strong>Manage MCP Servers</strong>,
                    then <strong>View raw config</strong> (opens <code>~/.gemini/antigravity/mcp_config.json</code>)
                  </li>
                </ol>
              </div>

              <v-divider class="my-4" />

              <div class="step-section">
                <div class="step-header">
                  <v-avatar size="22" color="primary" class="step-badge">
                    <span class="text-caption font-weight-bold">2</span>
                  </v-avatar>
                  <span class="text-body-2 font-weight-medium">Add the Continuum Design server</span>
                </div>
                <p class="text-body-2 mb-3 mt-2">
                  Paste this into the config (merge into <code>mcpServers</code> if you already have other servers):
                </p>
                <div class="code-block-wrapper">
                  <pre class="code-block">{{ antigravityConfig }}</pre>
                  <v-btn
                    size="x-small"
                    variant="tonal"
                    class="copy-btn"
                    @click="copyToClipboard(antigravityConfig)"
                  >
                    <v-icon size="14">mdi-content-copy</v-icon>
                  </v-btn>
                </div>
              </div>

              <p class="text-caption text-medium-emphasis mt-3">
                Replace <code>YOUR_API_KEY</code> with your generated API key. Antigravity IDE uses
                <code>serverUrl</code> (not <code>url</code>) for HTTP servers, and the key must be
                passed via the <code>Authorization: Bearer</code> header (the <code>x-api-key</code>
                header other clients use is not forwarded). Fully quit and reopen Antigravity IDE
                for the change to take effect.
              </p>

              <v-divider class="my-4" />

              <div class="step-section">
                <div class="step-header">
                  <v-avatar size="22" color="primary" class="step-badge">
                    <span class="text-caption font-weight-bold">3</span>
                  </v-avatar>
                  <span class="text-body-2 font-weight-medium">Set up Skills</span>
                  <v-chip size="x-small" variant="outlined" color="primary" class="ml-2">Optional</v-chip>
                </div>
                <p class="text-body-2 mb-3 mt-2">
                  Download each skill from the <strong>Continuum Design Skills</strong> section below,
                  then prepare the folders:
                </p>
                <ul class="text-body-2 setup-steps">
                  <li>
                    <strong>workflow-creation</strong> downloads as a zip. Unzip it and rename the
                    extracted folder to <code>workflow-creation</code>.
                  </li>
                  <li>
                    <strong>code-generation</strong> downloads as a single <code>SKILL.md</code>
                    file. Create a <code>code-generation</code> folder and put the
                    <code>SKILL.md</code> inside.
                  </li>
                </ul>

                <p class="text-caption text-medium-emphasis mt-2">
                  For the full list of skills, check the
                  <a :href="skillsRepoUrl" target="_blank" rel="noopener">qlerify-plugins repository</a>.
                </p>

                <p class="text-body-2 mt-3 mb-2">
                  Place the prepared folders in <strong>one</strong> of these locations:
                </p>
                <ul class="text-body-2 setup-steps">
                  <li>
                    <strong>Workspace skills</strong> — available only in this project.
                    Folder: <code>&lt;your-workspace&gt;/.agents/skills/</code>
                  </li>
                  <li>
                    <strong>Global skills</strong> — available across every workspace.
                    Folder: <code>~/.gemini/antigravity/skills/</code>
                  </li>
                </ul>
                <div class="code-block-wrapper mt-2">
                  <pre class="code-block">{{ antigravitySkillsLayout }}</pre>
                </div>

                <p class="text-caption text-medium-emphasis mt-3">
                  See the
                  <a href="https://antigravity.google/docs/skills" target="_blank" rel="noopener">official skills documentation</a>
                  for more details.
                </p>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <!-- Lovable -->
          <v-expansion-panel>
            <v-expansion-panel-title>
              <div class="d-flex align-center ga-2">
                <v-icon size="20">mdi-heart-outline</v-icon>
                <span class="font-weight-medium">Lovable</span>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <p class="text-body-2 mb-3">
                Add Continuum Design as a personal connector in Lovable:
              </p>
              <ol class="text-body-2 lovable-steps">
                <li>Go to <strong>Settings</strong> → <strong>Connectors</strong> → <strong>Personal connectors</strong></li>
                <li>Click <strong>Custom</strong></li>
                <li>
                  Set <strong>Server name</strong>: <code>Continuum Design</code>
                </li>
                <li>
                  Set <strong>Server URL</strong>:
                  <div class="code-block-wrapper mt-1">
                    <pre class="code-block">{{ mcpServerUrl }}</pre>
                    <v-btn
                      size="x-small"
                      variant="tonal"
                      class="copy-btn"
                      @click="copyToClipboard(mcpServerUrl)"
                    >
                      <v-icon size="14">mdi-content-copy</v-icon>
                    </v-btn>
                  </div>
                </li>
                <li>For authentication, select <strong>Bearer token or API key</strong></li>
                <li>Enter your Continuum Design API key</li>
                <li>Click <strong>Add server</strong></li>
              </ol>
              <p class="text-caption text-medium-emphasis mt-3">
                Custom MCP servers are available on Lovable paid plans.
              </p>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <!-- VS Code -->
          <v-expansion-panel>
            <v-expansion-panel-title>
              <div class="d-flex align-center ga-2">
                <v-icon size="20">mdi-microsoft-visual-studio-code</v-icon>
                <span class="font-weight-medium">VS Code</span>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <p class="text-body-2 mb-3">
                Add this to your MCP config file (<code>.vscode/mcp.json</code>):
              </p>
              <div class="code-block-wrapper">
                <pre class="code-block">{{ vscodeConfig }}</pre>
                <v-btn
                  size="x-small"
                  variant="tonal"
                  class="copy-btn"
                  @click="copyToClipboard(vscodeConfig)"
                >
                  <v-icon size="14">mdi-content-copy</v-icon>
                </v-btn>
              </div>
              <p class="text-caption text-medium-emphasis mt-3">
                Replace <code>YOUR_API_KEY</code> with your generated API key.
              </p>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <!-- Gemini CLI -->
          <v-expansion-panel>
            <v-expansion-panel-title>
              <div class="d-flex align-center ga-2">
                <v-icon size="20">mdi-google</v-icon>
                <span class="font-weight-medium">Gemini CLI</span>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <div class="step-section">
                <div class="step-header">
                  <v-avatar size="22" color="primary" class="step-badge">
                    <span class="text-caption font-weight-bold">1</span>
                  </v-avatar>
                  <span class="text-body-2 font-weight-medium">Connect MCP Server</span>
                </div>
                <p class="text-body-2 mb-3 mt-2">
                  Run this command in your terminal:
                </p>
                <div class="code-block-wrapper">
                  <pre class="code-block">{{ geminiCliCommand }}</pre>
                  <v-btn
                    size="x-small"
                    variant="tonal"
                    class="copy-btn"
                    @click="copyToClipboard(geminiCliCommand)"
                  >
                    <v-icon size="14">mdi-content-copy</v-icon>
                  </v-btn>
                </div>
                <p class="text-caption text-medium-emphasis mt-3">
                  Replace <code>YOUR_API_KEY</code> with your generated API key.
                </p>
              </div>

              <v-divider class="my-4" />

              <div class="step-section">
                <div class="step-header">
                  <v-avatar size="22" color="primary" class="step-badge">
                    <span class="text-caption font-weight-bold">2</span>
                  </v-avatar>
                  <span class="text-body-2 font-weight-medium">Install Plugins</span>
                  <v-chip size="x-small" variant="outlined" color="primary" class="ml-2">Optional</v-chip>
                </div>
                <p class="text-body-2 mb-3 mt-2">
                  Install Continuum Design plugins to enhance your AI assistant:
                </p>
                <div class="code-block-wrapper">
                  <pre class="code-block">{{ geminiPluginCommands }}</pre>
                  <v-btn
                    size="x-small"
                    variant="tonal"
                    class="copy-btn"
                    @click="copyToClipboard(geminiPluginCommands)"
                  >
                    <v-icon size="14">mdi-content-copy</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <!-- Claude Web -->
          <v-expansion-panel>
            <v-expansion-panel-title>
              <div class="d-flex align-center ga-2">
                <v-icon size="20">mdi-web</v-icon>
                <span class="font-weight-medium">Claude Web</span>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <p class="text-body-2 mb-3">
                Add Continuum Design as a custom connector. Sign-in happens via OAuth — no API key needed.
              </p>
              <ol class="text-body-2 setup-steps">
                <li>
                  Open <a href="https://claude.ai" target="_blank" rel="noopener">claude.ai</a> and go to <strong>Customize</strong> →
                  <strong>Connectors</strong>
                </li>
                <li>Click the <strong>+</strong> button → <strong>Add custom connector</strong></li>
                <li>
                  Set <strong>Name</strong>: <code>Continuum Design</code>
                </li>
                <li>
                  Set <strong>URL</strong>:
                  <div class="code-block-wrapper mt-1">
                    <pre class="code-block">{{ mcpOAuthUrl }}</pre>
                    <v-btn
                      size="x-small"
                      variant="tonal"
                      class="copy-btn"
                      @click="copyToClipboard(mcpOAuthUrl)"
                    >
                      <v-icon size="14">mdi-content-copy</v-icon>
                    </v-btn>
                  </div>
                </li>
                <li>Click <strong>Add</strong>, sign in with your Continuum Design account in the popup</li>
              </ol>
              <p class="text-caption text-medium-emphasis mt-3">
                Custom connectors are available on Pro, Max, Team, and Enterprise plans.
              </p>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <!-- Claude Desktop -->
          <v-expansion-panel>
            <v-expansion-panel-title>
              <div class="d-flex align-center ga-2">
                <v-icon size="20">mdi-monitor</v-icon>
                <span class="font-weight-medium">Claude Desktop</span>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <p class="text-body-2 mb-3">
                Same flow as Claude Web — add Continuum Design as a custom connector. Sign-in happens via OAuth.
              </p>
              <ol class="text-body-2 setup-steps">
                <li>Open Claude Desktop → <strong>Customize</strong> → <strong>Connectors</strong></li>
                <li>Click the <strong>+</strong> button → <strong>Add custom connector</strong></li>
                <li>
                  Set <strong>Name</strong>: <code>Continuum Design</code>
                </li>
                <li>
                  Set <strong>URL</strong>:
                  <div class="code-block-wrapper mt-1">
                    <pre class="code-block">{{ mcpOAuthUrl }}</pre>
                    <v-btn
                      size="x-small"
                      variant="tonal"
                      class="copy-btn"
                      @click="copyToClipboard(mcpOAuthUrl)"
                    >
                      <v-icon size="14">mdi-content-copy</v-icon>
                    </v-btn>
                  </div>
                </li>
                <li>Click <strong>Add</strong>, sign in with your Continuum Design account in the browser window that opens</li>
              </ol>
              <p class="text-caption text-medium-emphasis mt-3">
                Custom connectors are available on Pro, Max, Team, and Enterprise plans.
              </p>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <!-- ChatGPT -->
          <v-expansion-panel>
            <v-expansion-panel-title>
              <div class="d-flex align-center ga-2">
                <v-icon size="20">mdi-message-text-outline</v-icon>
                <span class="font-weight-medium">ChatGPT</span>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <p class="text-body-2 mb-3">
                Add Continuum Design as a custom connector. Sign-in happens via OAuth — no API key needed.
              </p>
              <ol class="text-body-2 setup-steps">
                <li>
                  In ChatGPT, enable <strong>Developer Mode</strong> under <strong>Settings</strong> → <strong>Apps & connectors</strong> →
                  <strong>Advanced</strong>
                </li>
                <li>Go to <strong>Settings</strong> → <strong>Connectors</strong> → <strong>Create</strong></li>
                <li>
                  Set <strong>Name</strong>: <code>Continuum Design</code>
                </li>
                <li>
                  Set <strong>Description</strong>: <code>Access your Continuum Design workflows and domain models.</code>
                </li>
                <li>
                  Set <strong>MCP Server URL</strong>:
                  <div class="code-block-wrapper mt-1">
                    <pre class="code-block">{{ mcpOAuthUrl }}</pre>
                    <v-btn
                      size="x-small"
                      variant="tonal"
                      class="copy-btn"
                      @click="copyToClipboard(mcpOAuthUrl)"
                    >
                      <v-icon size="14">mdi-content-copy</v-icon>
                    </v-btn>
                  </div>
                </li>
                <li>Set <strong>Authentication</strong>: <strong>OAuth</strong></li>
                <li>Confirm <strong>I trust this application</strong> and click <strong>Create</strong></li>
                <li>Sign in with your Continuum Design account in the popup that opens</li>
              </ol>
              <p class="text-caption text-medium-emphasis mt-3">
                Custom MCP connectors require ChatGPT Pro, Team, Enterprise, or Edu and Developer Mode enabled.
              </p>
            </v-expansion-panel-text>
          </v-expansion-panel>

          <!-- Continuum Design Skills (Claude Web / Desktop) -->
          <v-expansion-panel>
            <v-expansion-panel-title>
              <div class="d-flex align-center ga-2">
                <v-icon size="20">mdi-school-outline</v-icon>
                <span class="font-weight-medium">Continuum Design Skills</span>
                <v-chip size="x-small" variant="outlined" color="primary" class="ml-2">Claude Web / Desktop · Optional</v-chip>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <p class="text-body-2 mb-3">
                Skills are optional packs of guidance that teach Claude how to model with Continuum Design. Install one or both — Claude will
                activate them automatically based on what you ask.
              </p>

              <div class="skill-card">
                <div class="skill-card-header">
                  <span class="font-weight-medium">workflow-creation</span>
                </div>
                <p class="text-body-2 mb-2">
                  Guidance for creating new Continuum Design workflows: events, lanes, commands, read models, and domain events.
                </p>
                <v-btn
                  size="small"
                  variant="tonal"
                  color="primary"
                  prepend-icon="mdi-download"
                  :href="workflowCreationDownloadUrl"
                  target="_blank"
                  rel="noopener"
                >
                  Download skill
                </v-btn>
              </div>

              <div class="skill-card mt-3">
                <div class="skill-card-header">
                  <span class="font-weight-medium">code-generation</span>
                </div>
                <p class="text-body-2 mb-2">
                  Guidance for generating production-ready code from a Continuum Design domain model — scaffolding aggregates, APIs, and
                  the app on your target tech stack.
                </p>
                <v-btn
                  size="small"
                  variant="tonal"
                  color="primary"
                  prepend-icon="mdi-download"
                  :href="codeGenerationDownloadUrl"
                  target="_blank"
                  rel="noopener"
                >
                  Download skill
                </v-btn>
              </div>

              <v-divider class="my-4" />

              <p class="text-body-2 font-weight-medium mb-2">
                Install in Claude
              </p>
              <ol class="text-body-2 setup-steps">
                <li>
                  Open <a href="https://claude.ai" target="_blank" rel="noopener">claude.ai</a> (or Claude Desktop) and go to <strong>Customize</strong>
                  → <strong>Skills</strong>
                </li>
                <li>Click the <strong>+</strong> button → <strong>+ Create skill</strong></li>
                <li>Upload the ZIP you downloaded above</li>
                <li>Toggle the skill on</li>
              </ol>

              <p class="text-caption text-medium-emphasis mt-3">
                Custom skills are available on Pro, Max, Team, and Enterprise plans. The <code>sync</code> and <code>download</code> skills
                are Claude Code only — they need local file system access.
              </p>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>

      <v-divider class="flex-shrink-0" />

      <v-card-actions class="pa-4 flex-shrink-0">
        <v-spacer />
        <v-btn variant="tonal" @click="$emit('update:modelValue', false)">
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'

defineProps({
  modelValue: {
    type: Boolean,
    default: false
  }
})

defineEmits(['update:modelValue'])

const store = useStore()

const mcpServerUrl = process.env.VUE_APP_MCP_URL || 'https://your-mcp-server-url'
const mcpOAuthUrl = `${mcpServerUrl}/mcp`
const pluginsRepo = 'https://github.com/qlerify/qlerify-plugins.git'
const pluginsRepoUrl = 'https://github.com/qlerify/qlerify-plugins'
const skillsRepoUrl = 'https://github.com/qlerify/qlerify-plugins/tree/main/plugins/mcp-companion/skills'
const skillFolderUrl = (skillName) => `https://download-directory.github.io/?url=https://github.com/qlerify/qlerify-plugins/tree/main/plugins/mcp-companion/skills/${skillName}`
const workflowCreationDownloadUrl = skillFolderUrl('workflow-creation')
const codeGenerationDownloadUrl = skillFolderUrl('code-generation')

const claudeCodeCommand = computed(() => {
  return `claude mcp add --transport http continuum-design ${mcpServerUrl} \\
  --header "x-api-key: YOUR_API_KEY"`
})

const claudePluginCommands = computed(() => {
  return [
    '/plugin marketplace add qlerify/qlerify-plugins',
    '/plugin install mcp-companion@qlerify-plugins'
  ].join('\n')
})

const cursorConfig = computed(() => {
  return JSON.stringify({
    mcpServers: {
      'continuum-design': {
        url: mcpServerUrl,
        headers: {
          'x-api-key': 'YOUR_API_KEY'
        }
      }
    }
  }, null, 2)
})

const cursorCloneCommand = computed(() => {
  return `git clone ${pluginsRepo}`
})

const vscodeConfig = computed(() => {
  return JSON.stringify({
    servers: {
      'continuum-design': {
        type: 'http',
        url: mcpServerUrl,
        headers: {
          'x-api-key': 'YOUR_API_KEY'
        }
      }
    }
  }, null, 2)
})

const geminiCliCommand = computed(() => {
  return `gemini mcp add \\
  --transport http \\
  --header "x-api-key: YOUR_API_KEY" \\
  continuum-design "${mcpServerUrl}"`
})

const geminiPluginCommands = computed(() => {
  return [
    '# Sync skill',
    `gemini skills install ${pluginsRepo} \\`,
    '  --path plugins/mcp-companion/skills/sync',
    '',
    '# Download skill',
    `gemini skills install ${pluginsRepo} \\`,
    '  --path plugins/mcp-companion/skills/download',
    '',
    '# Workflow creation skill',
    `gemini skills install ${pluginsRepo} \\`,
    '  --path plugins/mcp-companion/skills/workflow-creation',
    '',
    '# Code generation skill',
    `gemini skills install ${pluginsRepo} \\`,
    '  --path plugins/mcp-companion/skills/code-generation'
  ].join('\n')
})

const skillTree = [
  '└── skills/',
  '    ├── workflow-creation/',
  '    │   ├── SKILL.md',
  '    │   ├── references/',
  '    │   └── examples/',
  '    └── code-generation/',
  '        └── SKILL.md'
]

const cursorSkillsLayout = ['.cursor/', ...skillTree].join('\n')
const antigravitySkillsLayout = ['.agents/', ...skillTree].join('\n')

const antigravityConfig = computed(() => {
  return JSON.stringify({
    mcpServers: {
      'continuum-design': {
        serverUrl: mcpServerUrl,
        headers: {
          Authorization: 'Bearer YOUR_API_KEY',
          'Content-Type': 'application/json'
        }
      }
    }
  }, null, 2)
})

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text)
    store.commit('snackbar/showMessage', {
      content: 'Copied to clipboard!',
      timeout: 2000,
      color: 'success'
    }, { root: true })
  } catch (err) {
    store.commit('snackbar/showMessage', {
      content: 'Failed to copy to clipboard.',
      timeout: 3000,
      color: 'error'
    }, { root: true })
  }
}
</script>

<style scoped>
.dots-chip {
  vertical-align: baseline;
  margin: 0 4px;
  padding: 0 8px;
  min-height: 20px;
}

.dots-chip :deep(.v-chip__content) {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
}

.code-block-wrapper {
  position: relative;
}

.code-block {
  font-family: 'Courier New', Courier, monospace;
  font-size: 12px;
  background-color: #1e1e1e;
  color: #d4d4d4;
  padding: 16px;
  padding-right: 48px;
  border-radius: 6px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  line-height: 1.5;
}

.copy-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  min-width: 28px !important;
  width: 28px;
  height: 28px;
  padding: 0 !important;
  background-color: rgba(255, 255, 255, 0.1) !important;
  color: #d4d4d4 !important;
}

.copy-btn:hover {
  background-color: rgba(255, 255, 255, 0.2) !important;
}

.step-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.step-badge {
  flex-shrink: 0;
}

.lovable-steps,
.setup-steps {
  padding-left: 20px;
  margin-bottom: 0;
}

.lovable-steps li,
.setup-steps li {
  margin-bottom: 8px;
}

.skill-card {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  padding: 12px 16px;
}

.skill-card-header {
  margin-bottom: 4px;
}
</style>
